import { ButtonHTMLAttributes, forwardRef, ReactNode } from "react";

import { cn } from "../utls/utils";

type BadgeButtonTone = "primary" | "neutral" | "success" | "danger";
type BadgeButtonVariant = "solid" | "soft" | "outline";
type BadgeButtonSize = "sm" | "md";

export interface BadgeButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: BadgeButtonTone;
  variant?: BadgeButtonVariant;
  size?: BadgeButtonSize;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  isActive?: boolean;
}

const sizeClasses: Record<BadgeButtonSize, string> = {
  sm: "px-3 py-1 text-xs",
  md: "px-3.5 py-1.5 text-sm",
};

const toneVariantClasses: Record<
  BadgeButtonTone,
  Record<BadgeButtonVariant, string>
> = {
  primary: {
    solid:
      "border-terracotta-600 bg-terracotta-500 text-white hover:bg-terracotta-600",
    soft:
      "border-terracotta-200 bg-terracotta-100 text-terracotta-700 hover:bg-terracotta-200",
    outline:
      "border-terracotta-400 bg-transparent text-terracotta-700 hover:bg-terracotta-50",
  },
  neutral: {
    solid:
      "border-brown-300 bg-brown-200 text-brown-800 hover:bg-brown-300",
    soft:
      "border-beige-300 bg-beige-100 text-brown-700 hover:bg-beige-200",
    outline:
      "border-brown-300 bg-transparent text-brown-700 hover:bg-cream-100",
  },
  success: {
    solid: "border-emerald-600 bg-emerald-500 text-white hover:bg-emerald-600",
    soft:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
    outline:
      "border-emerald-400 bg-transparent text-emerald-700 hover:bg-emerald-50",
  },
  danger: {
    solid: "border-danger/80 bg-danger text-white hover:bg-danger/90",
    soft: "border-danger/40 bg-danger/10 text-danger hover:bg-danger/20",
    outline:
      "border-danger/50 bg-transparent text-danger hover:bg-danger/10",
  },
};

const iconClasses =
  "inline-flex h-4 w-4 items-center justify-center text-current";

export const BadgeButton = forwardRef<HTMLButtonElement, BadgeButtonProps>(
  (
    {
      tone = "primary",
      variant = "soft",
      size = "md",
      leadingIcon,
      trailingIcon,
      isActive = false,
      className,
      children,
      type = "button",
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          "inline-flex items-center gap-2 rounded-full border font-medium transition",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-300 focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-60",
          sizeClasses[size],
          toneVariantClasses[tone][variant],
          isActive ? "ring-2 ring-terracotta-300 ring-offset-2" : "",
          className
        )}
        type={type}
        {...props}
      >
        {leadingIcon ? <span className={iconClasses}>{leadingIcon}</span> : null}
        <span className="truncate">{children}</span>
        {trailingIcon ? <span className={iconClasses}>{trailingIcon}</span> : null}
      </button>
    );
  }
);

BadgeButton.displayName = "BadgeButton";

export default BadgeButton;
